<script>
    import modalService from '@/services/core/modal.service';
    import Modal from '@/components/modals/Modal';
    import CompanySearchSelect from '@/components/shared/CompanySearchSelect';
    import ButtonSpinner from '@/components/shared/ButtonSpinner';
    import httpService from '@/services/core/http.service';
    import utilsService from '@/services/core/utils.service';

    const CHIP_LIMIT = 12;

    const SECTIONS = [
        {
            key: 'downtimeReasons',
            flag: 'copyDowntimeReasons',
            label: 'Downtime Reasons',
            noun: 'downtime reason',
            // Spelled out rather than noun + 's': "transport companys" is what that gets you.
            nounPlural: 'downtime reasons'
        },
        {
            key: 'transportCompanies',
            flag: 'copyTransportCompanies',
            label: 'Transport Companies',
            noun: 'transport company',
            nounPlural: 'transport companies'
        }
    ];

    export default {
        name: 'CopyCompanySettingsModal',
        props: {
            modalData: Object
        },
        components: { Modal, CompanySearchSelect, ButtonSpinner },
        data() {
            return {
                sections: SECTIONS,
                selected: { copyDowntimeReasons: true, copyTransportCompanies: true },
                destination: null,
                preview: null,
                previewError: '',
                isPreviewLoading: false,
                isSaving: false,
                // Only the newest preview request may write to the panel. Picking a second company
                // before the first response lands would otherwise leave that company's counts on
                // screen under the new company's name.
                previewToken: 0
            };
        },
        computed: {
            sourceName() {
                return this.modalData.item?.name;
            },
            sourceId() {
                return this.modalData.item?.id;
            },
            destinationName() {
                return this.destination?.name;
            },
            selectedSections() {
                return this.sections.filter(section => this.selected[section.flag]);
            },
            totalToCreate() {
                if (!this.preview) return 0;

                return this.selectedSections.reduce((total, section) => total + this.countFor(section, 'toCreateCount'), 0);
            },
            totalSkipped() {
                if (!this.preview) return 0;

                return this.selectedSections.reduce((total, section) => total + this.countFor(section, 'skippedCount'), 0);
            },
            canCopy() {
                return !!this.destination && !this.isPreviewLoading && !this.previewError && this.totalToCreate > 0;
            },
            summary() {
                if (!this.preview) return '';

                if (!this.selectedSections.length) return 'Select at least one section.';

                if (this.totalToCreate === 0) {
                    return this.totalSkipped > 0
                        ? `${this.destinationName} already has everything selected — nothing to add.`
                        : `${this.sourceName} has no ${this.selectedSections.map(section => section.label).join(' or ')} to copy.`;
                }

                const adds = `Adds ${this.describeTotal('toCreateCount')} to ${this.destinationName}.`;

                return this.totalSkipped > 0 ? `${adds} Nothing already there is changed.` : adds;
            }
        },
        methods: {
            close() {
                modalService.closeModal(true);
            },
            countFor(section, field) {
                return this.preview?.[section.key]?.[field] ?? 0;
            },
            namesFor(section, field) {
                return this.preview?.[section.key]?.[field] ?? [];
            },
            /** Chips shown inline. A company with sixty reasons must not stretch the modal. */
            chipsFor(section) {
                return this.namesFor(section, 'toCreate').slice(0, CHIP_LIMIT);
            },
            overflowFor(section) {
                return Math.max(0, this.countFor(section, 'toCreateCount') - CHIP_LIMIT);
            },
            overflowTitleFor(section) {
                return this.namesFor(section, 'toCreate').slice(CHIP_LIMIT).join(', ');
            },
            plural(count, singular, pluralForm) {
                return `${count} ${count === 1 ? singular : pluralForm}`;
            },
            /** "3 downtime reasons and 1 transport company", limited to the checked sections. */
            describeTotal(field) {
                const parts = this.selectedSections
                    .filter(section => this.countFor(section, field) > 0)
                    .map(section => this.plural(this.countFor(section, field), section.noun, section.nounPlural));

                if (parts.length < 2) return parts[0] || '';

                return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
            },
            loadPreview() {
                this.preview = null;
                this.previewError = '';

                if (!this.destination) {
                    this.isPreviewLoading = false;
                    return;
                }

                const token = ++this.previewToken;
                this.isPreviewLoading = true;

                httpService
                    .get(`companies/${this.sourceId}/copy-settings/preview?destinationCompanyId=${this.destination.id}`, null, {
                        customCatch: true
                    })
                    .then(data => {
                        if (token !== this.previewToken) return;
                        this.preview = data;
                    })
                    .catch(error => {
                        if (token !== this.previewToken) return;
                        this.previewError = error?.message || 'This company’s settings could not be read. Try again.';
                    })
                    .finally(() => {
                        if (token === this.previewToken) this.isPreviewLoading = false;
                    });
            },
            confirmAndCopy() {
                if (!this.canCopy) return;

                modalService.confirm({
                    title: 'Copy Settings',
                    msg: `Add ${this.describeTotal('toCreateCount')} to ${this.destinationName}? Entries it already has are left unchanged.`,
                    btnTextOk: 'Copy',
                    btnTextCancel: 'Cancel',
                    onSuccess: () => this.copy()
                });
            },
            copy() {
                this.isSaving = true;

                httpService
                    .post(`companies/${this.sourceId}/copy-settings?destinationCompanyId=${this.destination.id}`, {
                        copyDowntimeReasons: this.selected.copyDowntimeReasons,
                        copyTransportCompanies: this.selected.copyTransportCompanies
                    })
                    .then(result => {
                        const created = [
                            { count: result.downtimeReasonsCreated, section: SECTIONS[0] },
                            { count: result.transportCompaniesCreated, section: SECTIONS[1] }
                        ]
                            .filter(entry => entry.count > 0)
                            .map(entry => this.plural(entry.count, entry.section.noun, entry.section.nounPlural))
                            .join(' and ');

                        utilsService.showToast(
                            created
                                ? `Copied ${created} to ${this.destinationName}.`
                                : `${this.destinationName} already had everything. Nothing was copied.`
                        );

                        this.modalData.onSuccess && this.modalData.onSuccess();
                        modalService.closeModal();
                    })
                    .finally(() => (this.isSaving = false));
            }
        },
        watch: {
            destination() {
                this.loadPreview();
            }
        }
    };
</script>

<template>
    <Modal sizeClass="modal-md-plus">
        <form class="gsb-form" @submit.prevent="confirmAndCopy">
            <div class="modal-content">
                <div class="modal-header">
                    <h3 class="modal-title">Copy Settings</h3>
                </div>

                <div class="modal-body copy-settings">
                    <!-- Direction first. This screen is reached from the source company's row, and
                         reading it the wrong way round copies into the wrong tenant. -->
                    <div class="row mb-3">
                        <label class="col-sm-3 col-form-label">From</label>
                        <div class="col-sm d-flex align-items-center">
                            <span class="fw-semibold">{{ sourceName }}</span>
                        </div>
                    </div>

                    <div class="row mb-3">
                        <label class="col-sm-3 col-form-label" for="copySettingsDestination">To</label>
                        <div class="col-sm">
                            <CompanySearchSelect
                                v-model="destination"
                                inputId="copySettingsDestination"
                                placeholder="Search for a company…"
                            />
                        </div>
                    </div>

                    <div class="row mb-3">
                        <span class="col-sm-3 col-form-label">Include</span>
                        <div class="col-sm">
                            <div v-for="section in sections" :key="section.key" class="form-check">
                                <input
                                    class="form-check-input"
                                    type="checkbox"
                                    :id="`copy-${section.key}`"
                                    v-model="selected[section.flag]"
                                />
                                <label class="form-check-label" :for="`copy-${section.key}`">{{ section.label }}</label>
                            </div>
                        </div>
                    </div>

                    <div class="copy-settings-preview" aria-live="polite" :aria-busy="isPreviewLoading">
                        <p v-if="!destination" class="copy-settings-hint mb-0">Pick a destination company to see what would be added.</p>

                        <p v-else-if="previewError" class="copy-settings-error mb-0">{{ previewError }}</p>

                        <template v-else-if="isPreviewLoading">
                            <div v-for="section in sections" :key="section.key" class="copy-settings-section">
                                <div class="copy-settings-head">
                                    <span class="copy-settings-label">{{ section.label }}</span>
                                    <span class="copy-settings-skeleton" aria-hidden="true"></span>
                                </div>
                                <ul class="copy-settings-chips" aria-hidden="true">
                                    <li class="copy-settings-chip copy-settings-chip-ghost"></li>
                                    <li class="copy-settings-chip copy-settings-chip-ghost"></li>
                                    <li class="copy-settings-chip copy-settings-chip-ghost"></li>
                                </ul>
                            </div>
                            <p class="copy-settings-hint mb-0">Checking what {{ destinationName }} already has…</p>
                        </template>

                        <template v-else-if="preview">
                            <div
                                v-for="section in sections"
                                :key="section.key"
                                class="copy-settings-section"
                                :class="{ 'copy-settings-section-off': !selected[section.flag] }"
                            >
                                <div class="copy-settings-head">
                                    <span class="copy-settings-label">{{ section.label }}</span>
                                    <span class="copy-settings-counts">
                                        <span class="copy-settings-badge" :class="{ 'is-zero': !countFor(section, 'toCreateCount') }">
                                            {{ countFor(section, 'toCreateCount') }} to add
                                        </span>
                                        <span v-if="countFor(section, 'skippedCount')" class="copy-settings-kept">
                                            {{ countFor(section, 'skippedCount') }} already there
                                        </span>
                                    </span>
                                </div>

                                <ul v-if="countFor(section, 'toCreateCount')" class="copy-settings-chips">
                                    <li v-for="name in chipsFor(section)" :key="name" class="copy-settings-chip">{{ name }}</li>
                                    <li
                                        v-if="overflowFor(section)"
                                        class="copy-settings-chip copy-settings-chip-more"
                                        :title="overflowTitleFor(section)"
                                    >
                                        +{{ overflowFor(section) }} more
                                    </li>
                                </ul>
                            </div>

                            <p class="copy-settings-hint mb-0">{{ summary }}</p>
                        </template>
                    </div>
                </div>

                <div class="modal-footer d-flex justify-content-between">
                    <button type="button" class="btn" @click="close">Cancel</button>
                    <ButtonSpinner :isLoading="isSaving" :disabled="!canCopy" text="Copy" />
                </div>
            </div>
        </form>
    </Modal>
</template>

<style lang="scss" scoped>
    $accent: #23a89e;
    $accent-ink: #0f6b64;
    $accent-wash: #eaf6f5;
    $muted: #6b727a;
    $rule: #e9ecef;

    .copy-settings-preview {
        border-top: 1px solid $rule;
        padding-top: 0.875rem;
        margin-top: 0.25rem;
        min-height: 9rem;
    }

    .copy-settings-section {
        padding: 0.5rem 0 0.625rem;
        transition: opacity 0.15s ease;

        + .copy-settings-section {
            border-top: 1px solid #f4f6f8;
        }
    }

    .copy-settings-section-off {
        opacity: 0.4;
    }

    .copy-settings-head {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: 1rem;
    }

    .copy-settings-label {
        color: #444;
        font-weight: 600;
    }

    .copy-settings-counts {
        display: flex;
        align-items: baseline;
        gap: 0.625rem;
        white-space: nowrap;
    }

    .copy-settings-badge {
        background: $accent-wash;
        border-radius: 999px;
        color: $accent-ink;
        font-size: 0.8125rem;
        font-variant-numeric: tabular-nums;
        font-weight: 600;
        padding: 0.125rem 0.5rem;

        &.is-zero {
            background: #f1f3f5;
            color: $muted;
            font-weight: 500;
        }
    }

    .copy-settings-kept {
        color: $muted;
        font-size: 0.8125rem;
        font-variant-numeric: tabular-nums;
    }

    .copy-settings-chips {
        display: flex;
        flex-wrap: wrap;
        gap: 0.25rem;
        list-style: none;
        margin: 0.4375rem 0 0;
        padding: 0;
    }

    .copy-settings-chip {
        background: #f6f8f9;
        border: 1px solid #e6eaed;
        border-radius: 0.25rem;
        color: #495057;
        font-size: 0.8125rem;
        line-height: 1.3;
        max-width: 100%;
        overflow: hidden;
        padding: 0.125rem 0.4375rem;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .copy-settings-chip-more {
        background: transparent;
        border-style: dashed;
        color: $muted;
        cursor: default;
    }

    .copy-settings-chip-ghost {
        border-color: transparent;
        color: transparent;
        width: 5.5rem;
        animation: copy-settings-pulse 1.2s ease-in-out infinite;
    }

    .copy-settings-hint {
        color: $muted;
        font-size: 0.875rem;
        padding-top: 0.625rem;
    }

    .copy-settings-error {
        color: #b02a37;
        font-size: 0.875rem;
        padding-top: 0.625rem;
    }

    .copy-settings-skeleton {
        display: inline-block;
        width: 5rem;
        height: 0.75rem;
        border-radius: 999px;
        background: #e9ecef;
        animation: copy-settings-pulse 1.2s ease-in-out infinite;
    }

    @keyframes copy-settings-pulse {
        0%,
        100% {
            opacity: 1;
        }
        50% {
            opacity: 0.45;
        }
    }

    @media (prefers-reduced-motion: reduce) {
        .copy-settings-skeleton,
        .copy-settings-chip-ghost {
            animation: none;
        }
    }
</style>
